import { Archive, GitBranch } from 'lucide-react';
import { cn } from '@/lib/utils/cn';
import { Button } from '@/components/ui/button';
import { formatDate } from '@/lib/utils/format';

/**
 * SOW version pinner — T-0102 / E06-F01 §1.
 *
 * Sits at the top of the Baseline screen and lets the consultant flip
 * between the *current* SOW (the latest 'current'-status row in the chain)
 * and the *original* SOW (version_seq=0). Per the prototype's
 * `screens-modules-tabs.js → contractBaseline` the pinner is a compact
 * header strip with:
 *   - version label (e.g. "SOW v2 · effective 2026-04-18")
 *   - deliverable count badge
 *   - two-segment toggle (current ↔ original)
 *
 * The "original" toggle is informational in v1 — it switches the active
 * version label and lets the screen render the as-of EDL view; deep
 * comparison between the two versions lives on the Changes screen. When
 * only one version exists (no change orders applied), the toggle is hidden
 * and the strip just shows the current version.
 *
 * Tokens: paper-50 background with avero-orange left rule to mirror the
 * "active baseline" callout in the prototype. The button group uses the
 * default outline variant for unselected and the amber accent for selected.
 *
 * Design contract: `design_handoff_caliber_v2/prototype/screens-modules-
 * tabs.js` line 292-299 (the "SOW v2 · ingested" strip).
 */

export interface SowVersionPinnerProps {
  /** Version label, e.g. "v2" — used to render "SOW v2". */
  versionLabel: string;
  /**
   * Effective date (YYYY-MM-DD) of the active SOW, shown as "18 Apr 2026"
   * (C2-45). A non-date such as "not yet registered" is shown as given.
   */
  effectiveDate: string;
  /** Number of deliverables tracked under the active SOW. */
  deliverableCount: number;
  /** Total versions in the chain (used to decide whether to render the toggle). */
  versionsAvailable: number;
  /** Which version is currently active in the UI. */
  active: 'current' | 'original';
  /** Toggle callback. */
  onChange?: (next: 'current' | 'original') => void;
  /** Optional override for the section title (defaults to "SOW {versionLabel}"). */
  title?: string;
  className?: string;
}

export function SowVersionPinner({
  versionLabel,
  effectiveDate,
  deliverableCount,
  versionsAvailable,
  active,
  onChange,
  title,
  className,
}: SowVersionPinnerProps) {
  const heading = title ?? `SOW ${versionLabel}`;
  return (
    <div
      className={cn(
        'mb-4 flex items-center gap-4 rounded-card border border-ink-100 bg-paper-50 p-4',
        'border-l-[3px] border-l-accent-amber-600',
        className,
      )}
    >
      <span
        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-card bg-accent-amber-600 text-paper-0"
        aria-hidden="true"
      >
        <Archive className="h-4 w-4" />
      </span>
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-baseline gap-2">
          <span className="text-h3 font-semibold text-ink-900">{heading}</span>
          <span className="font-mono text-meta text-ink-500">
            effective {formatDate(effectiveDate)}
          </span>
        </div>
        <div className="mt-1 text-meta text-ink-500">
          {deliverableCount} deliverable{deliverableCount === 1 ? '' : 's'} tracked
          {versionsAvailable > 1 ? ` · ${versionsAvailable} versions in chain` : null}
        </div>
      </div>
      {versionsAvailable > 1 ? (
        <div
          className="inline-flex shrink-0 overflow-hidden rounded-card border border-ink-200"
          role="group"
          aria-label="SOW version"
        >
          <PinnerSegment
            label="Current"
            selected={active === 'current'}
            onClick={() => onChange?.('current')}
          />
          <PinnerSegment
            label="Original"
            selected={active === 'original'}
            onClick={() => onChange?.('original')}
          />
        </div>
      ) : (
        <Button type="button" variant="outline" size="sm" disabled className="shrink-0">
          <GitBranch className="h-3.5 w-3.5" aria-hidden="true" />
          Single version
        </Button>
      )}
    </div>
  );
}

function PinnerSegment({
  label,
  selected,
  onClick,
}: {
  label: string;
  selected: boolean;
  onClick?: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={selected}
      className={cn(
        'h-9 px-3 text-meta font-medium transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-amber-600',
        selected ? 'bg-accent-amber-600 text-paper-0' : 'bg-paper-0 text-ink-700 hover:bg-paper-50',
      )}
    >
      {label}
    </button>
  );
}
